import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChatContainer } from '@/components/chat/ChatContainer';
import { useUserStore } from '@/stores/userStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useChatStore } from '@/stores/chatStore';
import { CheckCircle, AlertCircle, X } from 'lucide-react';

type OAuthStatus = { type: 'success'; account: string } | { type: 'error'; message: string } | null;

export function ChatPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [oauthStatus, setOauthStatus] = useState<OAuthStatus>(null);
  const { initialize: initUser, isInitialized: userInitialized } = useUserStore();
  const { checkConnection } = useConnectionStore();
  const { addAssistantMessage, selectConversation } = useChatStore();

  useEffect(() => {
    initUser();
  }, [initUser]);

  useEffect(() => {
    if (userInitialized) {
      checkConnection();
    }
  }, [userInitialized, checkConnection]);

  // Handle deep-link to a specific conversation via ?c=<id>
  useEffect(() => {
    const convId = searchParams.get('c');
    if (convId && userInitialized) {
      selectConversation(convId);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, userInitialized, selectConversation]);

  // Handle OAuth callback
  useEffect(() => {
    const oauth = searchParams.get('oauth');
    const account = searchParams.get('account');
    const message = searchParams.get('message');

    if (oauth === 'success' && account) {
      setOauthStatus({ type: 'success', account });
      // Clear the query params
      setSearchParams({});
      // Refresh connection state
      checkConnection().then(() => {
        // Add a welcome message
        addAssistantMessage(`Connected to **${account}**! How can I help you configure your Zoom account?`);
      });
      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => setOauthStatus(null), 5000);
      return () => clearTimeout(timer);
    } else if (oauth === 'error') {
      setOauthStatus({ type: 'error', message: message ?? 'OAuth connection failed' });
      // Clear the query params
      setSearchParams({});
      // Auto-dismiss after 10 seconds
      const timer = setTimeout(() => setOauthStatus(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [searchParams, setSearchParams, checkConnection, addAssistantMessage]);

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">

      {/* OAuth status banner */}
      {oauthStatus?.type === 'success' && (
        <div className="bg-green-900/30 border-b border-green-700/50 px-4 py-2">
          <div className="mx-auto max-w-3xl flex items-center justify-between">
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm">Successfully connected to {oauthStatus.account}</span>
            </div>
            <button
              onClick={() => setOauthStatus(null)}
              className="text-green-400/70 hover:text-green-400"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {oauthStatus?.type === 'error' && (
        <div className="bg-red-900/30 border-b border-red-700/50 px-4 py-2">
          <div className="mx-auto max-w-3xl flex items-center justify-between">
            <div className="flex items-center gap-2 text-red-400">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">OAuth error: {oauthStatus.message}</span>
            </div>
            <button
              onClick={() => setOauthStatus(null)}
              className="text-red-400/70 hover:text-red-400"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <main className="flex-1 overflow-hidden">
        <ChatContainer />
      </main>
    </div>
  );
}
